export const LANGUAGE_FIELDS = {
  cultures: {
    fields: {
      fr: 'french_name',
      en: 'english_name',
      lat: 'latin_name',
    },
    labels: {
      fr: 'Français',
      en: 'Anglais',
      lat: 'Latin',
    },
    defaultLanguage: 'fr',
  },
};

// Formats a substance-name option as "Name (cas1, cas2)" using the CAS
// numbers the backend attaches via include_source_fields. When the substance
// has no CAS recorded, just the name is shown.
// R40 (27 mai 26): prefer `name_formatted` (inline markup preserved, e.g.
// italic *Bacillus*) for the DISPLAYED label; the option value stays the
// stripped `option.label` (the aggregation bucket key).
const formatSubstanceNameLabel = (option, source) => {
  const base = source?.name_formatted || option.label;
  const cas = source?.cas_numbers;
  const list = Array.isArray(cas)
    ? cas.filter((v) => v != null && String(v).trim() !== '')
    : cas
      ? [cas]
      : [];
  if (list.length === 0) return base;
  return `${base} (${list.join(', ')})`;
};

// R18 (27 mai 26): longer dropdowns load a bigger first page so keyboard
// arrow-down flows through more options before a page boundary (rc-select
// resets the active item to the top whenever the list length grows, so we
// reduce how often that happens without preloading the whole list).
const LONG_LIST_PAGE_SIZE = 75;

export const BASIC_FILTER_CONFIGS = [
  {
    key: 'names',
    labelKey: 'active-substance|filters.Active substance name',
    field: 'name.raw',
    pageSize: LONG_LIST_PAGE_SIZE,
    // Client spec: typing in this dropdown must match against the substance
    // name, its CAS number(s), and its synonyms. Object format enables the
    // generic "matched via" subtitle in the dropdown when the hit comes from
    // a secondary field.
    searchFields: [
      { field: 'name.keyword', primary: true },
      {
        field: 'cas_numbers.keyword',
        label: 'CAS',
        sourceField: 'cas_numbers',
      },
      { field: 'synonyms.keyword', label: 'Synonyme', sourceField: 'synonyms' },
    ],
    // Pulls CAS numbers (and synonyms, for completeness) into each option so
    // the label can be composed as "name (cas1, cas2)". `name_formatted`
    // carries the inline-formatted name (R40) for display.
    includeSourceFields: ['cas_numbers', 'synonyms', 'name_formatted'],
    formatOptionLabel: formatSubstanceNameLabel,
  },
  {
    key: 'cultures',
    labelKey: 'active-substance|filters.Cultures',
    field: 'cultures',
    value_field: 'id',
    label_field: 'french_name',
    pageSize: LONG_LIST_PAGE_SIZE,
    languageConfig: LANGUAGE_FIELDS.cultures,
    filters: {
      filter_hide_in_pc_ma_search: false,
      filter_show_in_customer_filter_MA: true,
    },
    // R44 (27 mai 26): typing letters should also match a culture's
    // synonyms in any of the three indexed languages; matches surface in
    // grey beneath the primary label via OptionRenderer's matchInfo.
    searchFields: [
      { field: 'cultures.french_name.keyword', primary: true },
      {
        field: 'cultures.french_name_synonyms.keyword',
        label: 'Synonyme',
        sourceField: 'cultures.french_name_synonyms',
      },
      {
        field: 'cultures.english_name_synonyms.keyword',
        label: 'Synonyme',
        sourceField: 'cultures.english_name_synonyms',
      },
      {
        field: 'cultures.latin_name_synonyms.keyword',
        label: 'Synonyme',
        sourceField: 'cultures.latin_name_synonyms',
      },
    ],
    // Searched name follows the language toggle (default searchFields only
    // covers French): searching "Apple" in English mode must match.
    searchFieldsByLanguage: {
      fr: ['cultures.french_name.keyword'],
      en: ['cultures.english_name.keyword'],
      lat: ['cultures.latin_name.keyword'],
    },
    includeSourceFields: [
      'cultures.french_name_synonyms',
      'cultures.english_name_synonyms',
      'cultures.latin_name_synonyms',
    ],
  },
  {
    key: 'cultures_practices',
    labelKey: 'active-substance|filters.Cultural practices',
    field: 'cultures.cultural_practices',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'activity_sectors',
    labelKey: 'active-substance|filters.Activity sector',
    field: 'activity_sectors',
    value_field: 'id',
    label_field: 'name',
  },
];

export const AGRONOMIC_FILTER_CONFIGS = [
  {
    key: 'chemical_families',
    labelKey:
      'active-substance|filters.Chemical families or biological families',
    field: 'pesticide_group_associations.chemical_family',
    value_field: 'id',
    label_field: 'label',
  },
  {
    key: 'pesticide_groups',
    labelKey: 'active-substance|filters.Pesticide groups',
    field: 'pesticide_group_associations.pesticide_group',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'pesticide_categories',
    labelKey: 'active-substance|filters.Pesticide categories',
    field: 'pesticide_categories',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'pesticide_types',
    labelKey: 'active-substance|filters.Pesticide types',
    field: 'pesticide_types',
    value_field: 'id',
    label_field: 'label',
  },
  {
    key: 'activity_types',
    labelKey: 'active-substance|filters.Activity types',
    field: 'activity_type_filters',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'arla_culture_groups',
    labelKey: 'active-substance|filters.ARLA culture groups',
    field: 'cultures.arla_culture_group',
    value_field: 'id',
    label_field: 'group',
  },
  {
    key: 'resistance',
    labelKey: 'active-substance|filters.Resistance',
    field:
      'pesticide_group_associations.pesticide_group.risk_of_resistance_development',
    value_field: 'id',
    label_field: 'name',
  },
];

// R7 (27 mai 26): symbol filters' ES buckets are raw integers (1, 2, 3, …).
// Without a label map the user typing "fai" never matches "2". Map each
// calculated_value to its French label and force the "least → worst" order
// from the client spec (rules 104/106/108/110/112/114/116).
const SYMBOL_LABELS_6 = {
  1: 'Aucun',
  2: 'Faible',
  3: 'Léger',
  4: 'Modéré',
  5: 'Élevé',
  6: 'Extrêmement élevé',
};
const SYMBOL_ORDER_6 = [1, 2, 3, 4, 5, 6];

const SYMBOL_LABELS_5 = {
  1: 'Aucun',
  2: 'Faible',
  3: 'Modéré',
  4: 'Élevé',
  5: 'Extrêmement élevé',
};
const SYMBOL_ORDER_5 = [1, 2, 3, 4, 5];

const SYMBOL_LABELS_4 = {
  1: 'Aucun',
  2: 'Faible',
  3: 'Modéré',
  4: 'Élevé',
};
const SYMBOL_ORDER_4 = [1, 2, 3, 4];

export const HEALTH_FILTER_CONFIGS = [
  {
    key: 'acute_toxicity',
    labelKey: 'active-substance|filters.Acute toxicity',
    field: 'symbols.health.acute_toxicity_symbol.calculated_value',
    valueLabels: SYMBOL_LABELS_6,
    valueOrder: SYMBOL_ORDER_6,
  },
  {
    key: 'long_term_effects',
    labelKey: 'active-substance|filters.Long term effects',
    field: 'symbols.health.long_term_effect_symbol.calculated_value',
    valueLabels: SYMBOL_LABELS_5,
    valueOrder: SYMBOL_ORDER_5,
  },
];

export const ENVIRONMENT_FILTER_CONFIGS = [
  {
    key: 'fish_or_daphnia',
    labelKey: 'active-substance|filters.Fish or daphnia',
    field: 'symbols.environment.fishes_symbol.calculated_value',
    valueLabels: SYMBOL_LABELS_6,
    valueOrder: SYMBOL_ORDER_6,
  },
  {
    key: 'birds',
    labelKey: 'active-substance|filters.Birds',
    field: 'symbols.environment.birds_symbol.calculated_value',
    valueLabels: SYMBOL_LABELS_6,
    valueOrder: SYMBOL_ORDER_6,
  },
  {
    key: 'bees',
    labelKey: 'active-substance|filters.Bees',
    field: 'symbols.environment.bees_symbol.calculated_value',
    valueLabels: SYMBOL_LABELS_4,
    valueOrder: SYMBOL_ORDER_4,
  },
  {
    key: 'persistence',
    labelKey: 'active-substance|filters.Persistence',
    field: 'symbols.environment.persistence_symbol.calculated_value',
    valueLabels: SYMBOL_LABELS_4,
    valueOrder: SYMBOL_ORDER_4,
  },
  {
    key: 'leaching_potential',
    labelKey: 'active-substance|filters.Leaching potential',
    field: 'symbols.environment.leaching_symbol.calculated_value',
    valueLabels: SYMBOL_LABELS_4,
    valueOrder: SYMBOL_ORDER_4,
  },
];

export const BASIC_FILTER_KEYS = BASIC_FILTER_CONFIGS.map((c) => c.key);
export const EXTRA_FILTER_KEYS = [
  ...AGRONOMIC_FILTER_CONFIGS.map((c) => c.key),
  ...HEALTH_FILTER_CONFIGS.map((c) => c.key),
  ...ENVIRONMENT_FILTER_CONFIGS.map((c) => c.key),
];

// Union of every configurable filter — used by FilterSection to compute the
// cross-field facet context passed to each ElasticsearchSelect.
export const ALL_FILTER_CONFIGS = [
  ...BASIC_FILTER_CONFIGS,
  ...AGRONOMIC_FILTER_CONFIGS,
  ...HEALTH_FILTER_CONFIGS,
  ...ENVIRONMENT_FILTER_CONFIGS,
];

export const DOCUMENT_TYPE = 'SubstanceActiveDocument';
